import { useEffect, useState } from "react"
import type { FormEvent } from "react"
import { Plus } from "lucide-react"
import Card from "../components/Card"
import StatusPill from "../components/StatusPill"
import InviteUserModal from "../components/InviteUserModal"
import { useAuth } from "../auth/AuthProvider"
import type { AsanaIntegration, Brand } from "../types"

type TeamMember = { id: string; name: string; email: string; role: string; status: string }
type BrandsResponse = { brands?: Brand[] }
type AsanaIntegrationResponse = { integration?: AsanaIntegration; error?: string }

const SettingsPage = () => {
  const { hasRole, user } = useAuth()
  const isAdmin = hasRole(["Admin"])
  const [isInviteOpen, setIsInviteOpen] = useState(false)
  const [currency, setCurrency] = useState(user?.currency || "USD")
  const [timezone, setTimezone] = useState(user?.timezone || "America/Los_Angeles")
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null)
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  })
  const [passwordSaving, setPasswordSaving] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null)
  const [brands, setBrands] = useState<Brand[]>([])
  const [editingBrandId, setEditingBrandId] = useState("")
  const [brandSaving, setBrandSaving] = useState(false)
  const [brandError, setBrandError] = useState<string | null>(null)
  const [brandSuccess, setBrandSuccess] = useState<string | null>(null)
  const [brandForm, setBrandForm] = useState({
    name: "",
    legalName: "",
    dbaName: "",
    termsUrl: "",
    primaryColor: "",
    logoUrl: "",
    billingEmail: "",
    billingAddress: "",
    postSignatureContent: "",
    isActive: true,
  })
  const [asanaIntegration, setAsanaIntegration] = useState<AsanaIntegration | null>(null)
  const [asanaSaving, setAsanaSaving] = useState(false)
  const [asanaTesting, setAsanaTesting] = useState(false)
  const [asanaError, setAsanaError] = useState<string | null>(null)
  const [asanaSuccess, setAsanaSuccess] = useState<string | null>(null)
  const [asanaForm, setAsanaForm] = useState({
    workspaceGid: "",
    workspaceName: "",
    selectedProjectGids: [] as string[],
    includeTemplates: true,
    syncEnabled: false,
    token: "",
  })

  useEffect(() => {
    if (user?.timezone) setTimezone(user.timezone)
    if (user?.currency) setCurrency(user.currency)
  }, [user?.timezone, user?.currency])

  useEffect(() => {
    fetch("/api/users").then(r => r.json()).then(d => {
      if (d.users) setTeamMembers(d.users)
    }).catch(() => {})
  }, [])

  const loadBrands = async () => {
    if (!isAdmin) return
    try {
      const response = await fetch("/api/brands")
      const data = (await response.json().catch(() => ({}))) as BrandsResponse
      setBrands(data.brands || [])
    } catch {
      setBrands([])
    }
  }

  const loadAsanaIntegration = async () => {
    if (!isAdmin) return
    try {
      const response = await fetch("/api/integrations/asana")
      const data = (await response.json().catch(() => ({}))) as AsanaIntegrationResponse
      if (!response.ok || !data.integration) {
        throw new Error(data.error || "Failed to load Asana integration")
      }

      setAsanaIntegration(data.integration)
      setAsanaForm((current) => ({
        ...current,
        workspaceGid: data.integration?.config.workspaceGid || "",
        workspaceName: data.integration?.config.workspaceName || "",
        selectedProjectGids: data.integration?.config.selectedProjectGids || [],
        includeTemplates: data.integration?.config.includeTemplates ?? true,
        syncEnabled: data.integration?.config.syncEnabled ?? false,
        token: "",
      }))
    } catch {
      setAsanaIntegration(null)
    }
  }

  useEffect(() => {
    loadBrands()
    loadAsanaIntegration()
  }, [isAdmin]) // eslint-disable-line react-hooks/exhaustive-deps

  const savePreference = async (field: string, value: string) => {
    if (!user) return
    setSaving(true)
    setSaveError(null)
    setSaveSuccess(null)
    try {
      const res = await fetch(`/api/users/${user.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [field]: value }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || "Failed to save preference")
      }
      setSaveSuccess("Preferences saved")
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to save preference"
      setSaveError(message)
    } finally {
      setSaving(false)
    }
  }

  const handlePasswordSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setPasswordError(null)
    setPasswordSuccess(null)

    if (passwordForm.newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters.")
      return
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New password and confirmation do not match.")
      return
    }

    setPasswordSaving(true)
    try {
      const res = await fetch("/api/auth/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        throw new Error(data.error || "Failed to update password")
      }
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" })
      setPasswordSuccess("Password updated. Please sign in again.")
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to update password"
      setPasswordError(message)
    } finally {
      setPasswordSaving(false)
    }
  }

  const resetBrandForm = () => {
    setEditingBrandId("")
    setBrandForm({
      name: "",
      legalName: "",
      dbaName: "",
      termsUrl: "",
      primaryColor: "",
      logoUrl: "",
      billingEmail: "",
      billingAddress: "",
      postSignatureContent: "",
      isActive: true,
    })
  }

  const handleBrandSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setBrandSaving(true)
    setBrandError(null)
    setBrandSuccess(null)
    try {
      const response = await fetch(editingBrandId ? `/api/brands/${editingBrandId}` : "/api/brands", {
        method: editingBrandId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(brandForm),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(data.error || "Failed to save brand")
      }
      setBrandSuccess(editingBrandId ? "Brand updated." : "Brand created.")
      resetBrandForm()
      await loadBrands()
    } catch (submitError) {
      setBrandError(submitError instanceof Error ? submitError.message : "Failed to save brand")
    } finally {
      setBrandSaving(false)
    }
  }

  const startBrandEdit = (brand: Brand) => {
    setEditingBrandId(brand.id)
    setBrandError(null)
    setBrandSuccess(null)
    setBrandForm({
      name: brand.name,
      legalName: brand.legalName,
      dbaName: brand.dbaName,
      termsUrl: brand.termsUrl,
      primaryColor: brand.primaryColor,
      logoUrl: brand.logoUrl,
      billingEmail: brand.billingEmail,
      billingAddress: brand.billingAddress,
      postSignatureContent: brand.postSignatureContent || "",
      isActive: brand.isActive,
    })
  }

  const handleAsanaSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setAsanaSaving(true)
    setAsanaError(null)
    setAsanaSuccess(null)
    try {
      const selectedProjects = (asanaIntegration?.metadata.projects || []).filter((project) =>
        asanaForm.selectedProjectGids.includes(project.gid),
      )
      const response = await fetch("/api/integrations/asana", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          workspaceGid: asanaForm.workspaceGid,
          workspaceName: asanaForm.workspaceName,
          selectedProjectGids: asanaForm.selectedProjectGids,
          selectedProjects,
          includeTemplates: asanaForm.includeTemplates,
          syncEnabled: asanaForm.syncEnabled,
          token: asanaForm.token || undefined,
        }),
      })
      const data = (await response.json().catch(() => ({}))) as AsanaIntegrationResponse
      if (!response.ok || !data.integration) {
        throw new Error(data.error || "Failed to save Asana integration")
      }

      setAsanaIntegration(data.integration)
      setAsanaForm((current) => ({ ...current, token: "" }))
      setAsanaSuccess("Asana settings saved.")
    } catch (submitError) {
      setAsanaError(submitError instanceof Error ? submitError.message : "Failed to save Asana integration")
    } finally {
      setAsanaSaving(false)
    }
  }

  const handleAsanaTest = async () => {
    setAsanaTesting(true)
    setAsanaError(null)
    setAsanaSuccess(null)
    try {
      const response = await fetch("/api/integrations/asana", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          workspaceGid: asanaForm.workspaceGid || undefined,
          workspaceName: asanaForm.workspaceName || undefined,
          selectedProjectGids: asanaForm.selectedProjectGids,
          includeTemplates: asanaForm.includeTemplates,
          syncEnabled: asanaForm.syncEnabled,
          token: asanaForm.token || undefined,
        }),
      })
      const data = (await response.json().catch(() => ({}))) as AsanaIntegrationResponse
      if (!response.ok || !data.integration) {
        throw new Error(data.error || "Failed to connect to Asana")
      }

      setAsanaIntegration(data.integration)
      setAsanaForm((current) => ({
        ...current,
        workspaceGid: data.integration?.config.workspaceGid || current.workspaceGid,
        workspaceName: data.integration?.config.workspaceName || current.workspaceName,
        selectedProjectGids: data.integration?.config.selectedProjectGids || current.selectedProjectGids,
        includeTemplates: data.integration?.config.includeTemplates ?? current.includeTemplates,
        syncEnabled: data.integration?.config.syncEnabled ?? current.syncEnabled,
        token: "",
      }))
      setAsanaSuccess("Connected to Asana and refreshed workspace details.")
    } catch (submitError) {
      setAsanaError(submitError instanceof Error ? submitError.message : "Failed to connect to Asana")
    } finally {
      setAsanaTesting(false)
    }
  }

  const handleClearStoredAsanaToken = async () => {
    setAsanaSaving(true)
    setAsanaError(null)
    setAsanaSuccess(null)
    try {
      const response = await fetch("/api/integrations/asana", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          workspaceGid: asanaForm.workspaceGid,
          workspaceName: asanaForm.workspaceName,
          selectedProjectGids: asanaForm.selectedProjectGids,
          selectedProjects: (asanaIntegration?.metadata.projects || []).filter((project) =>
            asanaForm.selectedProjectGids.includes(project.gid),
          ),
          includeTemplates: asanaForm.includeTemplates,
          syncEnabled: asanaForm.syncEnabled,
          clearStoredToken: true,
        }),
      })
      const data = (await response.json().catch(() => ({}))) as AsanaIntegrationResponse
      if (!response.ok || !data.integration) {
        throw new Error(data.error || "Failed to clear stored Asana token")
      }

      setAsanaIntegration(data.integration)
      setAsanaSuccess("Stored Asana token cleared.")
    } catch (submitError) {
      setAsanaError(submitError instanceof Error ? submitError.message : "Failed to clear stored Asana token")
    } finally {
      setAsanaSaving(false)
    }
  }

  const toggleAsanaProject = (projectGid: string) => {
    setAsanaForm((current) => ({
      ...current,
      selectedProjectGids: current.selectedProjectGids.includes(projectGid)
        ? current.selectedProjectGids.filter((value) => value !== projectGid)
        : [...current.selectedProjectGids, projectGid],
    }))
  }

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-lg font-semibold text-slate-900">Settings</p>
            <p className="text-sm text-stone">
              Manage roles, permissions, integrations, and workspace preferences.
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={() => setIsInviteOpen(true)}
              className="flex items-center gap-2 rounded-full bg-moss px-4 py-2 text-sm font-semibold text-white shadow-soft transition hover:bg-moss/90"
            >
              <Plus className="h-4 w-4" />
              Invite User
            </button>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <p className="text-lg font-semibold text-slate-900">Roles & permissions</p>
          <div className="mt-4 space-y-3 text-sm">
            <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
              <div>
                <p className="font-semibold text-slate-900">Admin</p>
                <p className="text-xs text-stone">Full access to billing, reports, payouts</p>
              </div>
              <StatusPill tone="success" label="All modules" />
            </div>
            <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
              <div>
                <p className="font-semibold text-slate-900">Staff</p>
                <p className="text-xs text-stone">Tasks, projects, time tracking</p>
              </div>
              <StatusPill tone="neutral" label="Limited" />
            </div>
            <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
              <div>
                <p className="font-semibold text-slate-900">Contractor</p>
                <p className="text-xs text-stone">Time tracking + earnings only</p>
              </div>
              <StatusPill tone="warning" label="Time only" />
            </div>
          </div>
        </Card>

        <Card>
          <p className="text-lg font-semibold text-slate-900">Workspace preferences</p>
          <div className="mt-4 space-y-3 text-sm">
            <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
              <span className="text-stone">Currency</span>
              <select
                value={currency}
                onChange={(e) => { setCurrency(e.target.value); savePreference("currency", e.target.value) }}
                disabled={saving}
                className="rounded-lg border border-line bg-white px-3 py-1 text-sm font-semibold text-slate-900 outline-none disabled:opacity-50"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="CAD">CAD</option>
                <option value="AUD">AUD</option>
              </select>
            </div>
            <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
              <span className="text-stone">Time zone</span>
              <select
                value={timezone}
                onChange={(e) => { setTimezone(e.target.value); savePreference("timezone", e.target.value) }}
                disabled={saving}
                className="rounded-lg border border-line bg-white px-3 py-1 text-sm font-semibold text-slate-900 outline-none disabled:opacity-50"
              >
                <option value="America/Los_Angeles">Pacific (LA)</option>
                <option value="America/Denver">Mountain (Denver)</option>
                <option value="America/Chicago">Central (Chicago)</option>
                <option value="America/New_York">Eastern (NY)</option>
                <option value="Europe/London">London</option>
                <option value="Europe/Berlin">Berlin</option>
                <option value="Asia/Tokyo">Tokyo</option>
              </select>
            </div>
            {saveError && <p className="text-xs text-rose-600">{saveError}</p>}
            {saveSuccess && <p className="text-xs text-emerald-700">{saveSuccess}</p>}
          </div>
        </Card>

        {user && (
          <Card>
            <p className="text-lg font-semibold text-slate-900">Your profile</p>
            <div className="mt-4 space-y-3 text-sm">
              <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
                <span className="text-stone">Name</span>
                <span className="font-semibold text-slate-900">{user.name}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
                <span className="text-stone">Email</span>
                <span className="font-semibold text-slate-900">{user.email}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
                <span className="text-stone">Role</span>
                <StatusPill tone="success" label={user.role} />
              </div>
            </div>
          </Card>
        )}

        {user && (
          <Card>
            <p className="text-lg font-semibold text-slate-900">Security</p>
            <p className="mt-1 text-sm text-stone">Change your password.</p>
            <form onSubmit={handlePasswordSubmit} className="mt-4 space-y-3 text-sm">
              <input
                type="password"
                required
                value={passwordForm.currentPassword}
                onChange={(e) =>
                  setPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }))
                }
                placeholder="Current password"
                className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
              />
              <input
                type="password"
                required
                minLength={8}
                value={passwordForm.newPassword}
                onChange={(e) =>
                  setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))
                }
                placeholder="New password (min 8)"
                className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
              />
              <input
                type="password"
                required
                minLength={8}
                value={passwordForm.confirmPassword}
                onChange={(e) =>
                  setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))
                }
                placeholder="Confirm new password"
                className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
              />
              {passwordError && <p className="text-xs text-rose-600">{passwordError}</p>}
              {passwordSuccess && <p className="text-xs text-emerald-700">{passwordSuccess}</p>}
              <button
                type="submit"
                disabled={passwordSaving}
                className="rounded-full bg-moss px-4 py-2 text-sm font-semibold text-white transition hover:bg-moss/90 disabled:opacity-50"
              >
                {passwordSaving ? "Saving..." : "Update password"}
              </button>
            </form>
          </Card>
        )}

        {isAdmin && (
          <Card className="lg:col-span-2">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-lg font-semibold text-slate-900">Brands & DBAs</p>
                <p className="text-sm text-stone">
                  Keep the general terms aligned while swapping the terms link and entity details for each brand.
                </p>
              </div>
              <StatusPill tone="neutral" label={`${brands.length} brands`} />
            </div>

            <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr,0.9fr]">
              <div className="space-y-3">
                {brands.map((brand) => (
                  <button
                    key={brand.id}
                    type="button"
                    onClick={() => startBrandEdit(brand)}
                    className="w-full rounded-2xl border border-line bg-slate-50 px-4 py-4 text-left transition hover:bg-slate-100"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-900">{brand.name}</p>
                        <p className="text-xs text-stone">{brand.legalName}</p>
                        <p className="mt-1 text-xs text-stone">{brand.termsUrl}</p>
                      </div>
                      <StatusPill tone={brand.isActive ? "success" : "danger"} label={brand.isActive ? "Active" : "Inactive"} />
                    </div>
                  </button>
                ))}
                {brands.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-line px-4 py-6 text-sm text-stone">
                    No brands configured yet.
                  </div>
                )}
              </div>

              <form onSubmit={handleBrandSubmit} className="space-y-3 text-sm">
                <input
                  type="text"
                  required
                  value={brandForm.name}
                  onChange={(e) => setBrandForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Brand name"
                  className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                />
                <input
                  type="text"
                  required
                  value={brandForm.legalName}
                  onChange={(e) => setBrandForm((prev) => ({ ...prev, legalName: e.target.value }))}
                  placeholder="Legal entity name"
                  className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                />
                <input
                  type="text"
                  value={brandForm.dbaName}
                  onChange={(e) => setBrandForm((prev) => ({ ...prev, dbaName: e.target.value }))}
                  placeholder="DBA name"
                  className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                />
                <input
                  type="url"
                  required
                  value={brandForm.termsUrl}
                  onChange={(e) => setBrandForm((prev) => ({ ...prev, termsUrl: e.target.value }))}
                  placeholder="Terms link"
                  className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={brandForm.primaryColor}
                    onChange={(e) => setBrandForm((prev) => ({ ...prev, primaryColor: e.target.value }))}
                    placeholder="Primary color"
                    className="rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                  />
                  <input
                    type="url"
                    value={brandForm.logoUrl}
                    onChange={(e) => setBrandForm((prev) => ({ ...prev, logoUrl: e.target.value }))}
                    placeholder="Logo URL"
                    className="rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                  />
                </div>
                <input
                  type="email"
                  value={brandForm.billingEmail}
                  onChange={(e) => setBrandForm((prev) => ({ ...prev, billingEmail: e.target.value }))}
                  placeholder="Billing email"
                  className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                />
                <textarea
                  rows={3}
                  value={brandForm.billingAddress}
                  onChange={(e) => setBrandForm((prev) => ({ ...prev, billingAddress: e.target.value }))}
                  placeholder="Billing address"
                  className="w-full rounded-xl border border-line bg-white px-4 py-3 outline-none focus:border-moss"
                />
                <div>
                  <label className="mb-1 block text-xs text-stone">Post-signature content (Meetings, Background IP, etc.)</label>
                  <textarea
                    rows={8}
                    value={brandForm.postSignatureContent}
                    onChange={(e) => setBrandForm((prev) => ({ ...prev, postSignatureContent: e.target.value }))}
                    placeholder={"## Meetings\nYour onboarding will begin with...\n\n## Background IP\n{{brand_dba}}'s documented processes..."}
                    className="w-full rounded-xl border border-line bg-white px-4 py-3 font-mono text-xs outline-none focus:border-moss"
                  />
                  <p className="mt-1 text-xs text-stone">
                    Use ## for section headings. Placeholders: {"{{brand_dba}}"}, {"{{brand_name}}"}, {"{{brand_legal}}"}. Conditionals: {"{{#if only_hosting}}...{{/if}}"}, {"{{#unless only_hosting}}...{{/unless}}"}
                  </p>
                </div>
                <label className="flex items-center gap-2 rounded-xl border border-line bg-slate-50 px-4 py-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={brandForm.isActive}
                    onChange={(e) => setBrandForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                  />
                  Brand is active
                </label>
                {brandError && <p className="text-xs text-rose-600">{brandError}</p>}
                {brandSuccess && <p className="text-xs text-emerald-700">{brandSuccess}</p>}
                <div className="flex flex-wrap gap-2">
                  <button
                    type="submit"
                    disabled={brandSaving}
                    className="rounded-full bg-moss px-4 py-2 text-sm font-semibold text-white transition hover:bg-moss/90 disabled:opacity-50"
                  >
                    {brandSaving ? "Saving..." : editingBrandId ? "Update brand" : "Add brand"}
                  </button>
                  {editingBrandId && (
                    <button
                      type="button"
                      onClick={resetBrandForm}
                      className="rounded-full border border-line px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      Cancel edit
                    </button>
                  )}
                </div>
              </form>
            </div>
          </Card>
        )}

        {isAdmin && (
          <Card className="lg:col-span-2">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-lg font-semibold text-slate-900">Asana integration</p>
                <p className="text-sm text-stone">
                  Configure the read-only Asana connection, workspace, and project filters from the admin dashboard.
                </p>
              </div>
              <StatusPill
                tone={
                  asanaIntegration?.status === "Connected"
                    ? "success"
                    : asanaIntegration?.status === "Error"
                      ? "danger"
                      : asanaIntegration?.status === "Configured"
                        ? "warning"
                        : "neutral"
                }
                label={asanaIntegration?.status || "Disabled"}
              />
            </div>

            <form onSubmit={handleAsanaSave} className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-[1.05fr,0.95fr]">
              <div className="space-y-3 text-sm">
                <input
                  type="password"
                  value={asanaForm.token}
                  onChange={(e) => setAsanaForm((prev) => ({ ...prev, token: e.target.value }))}
                  placeholder={
                    asanaIntegration?.hasToken
                      ? "Leave blank to keep existing token"
                      : "Asana personal access token"
                  }
                  className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                />
                {!asanaIntegration?.canStoreToken && (
                  <p className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700">
                    Token storage in the app requires `INTEGRATION_ENCRYPTION_KEY`. You can still test with a one-time token or use the `ASANA_PAT` environment secret.
                  </p>
                )}

                {(asanaIntegration?.metadata.workspaces.length || 0) > 0 ? (
                  <select
                    value={asanaForm.workspaceGid}
                    onChange={(e) => {
                      const workspace = asanaIntegration?.metadata.workspaces.find((entry) => entry.gid === e.target.value)
                      setAsanaForm((prev) => ({
                        ...prev,
                        workspaceGid: e.target.value,
                        workspaceName: workspace?.name || "",
                        selectedProjectGids: [],
                      }))
                    }}
                    className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                  >
                    <option value="">Select workspace</option>
                    {asanaIntegration?.metadata.workspaces.map((workspace) => (
                      <option key={workspace.gid} value={workspace.gid}>
                        {workspace.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={asanaForm.workspaceGid}
                    onChange={(e) => setAsanaForm((prev) => ({ ...prev, workspaceGid: e.target.value }))}
                    placeholder="Workspace GID"
                    className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                  />
                )}

                <input
                  type="text"
                  value={asanaForm.workspaceName}
                  onChange={(e) => setAsanaForm((prev) => ({ ...prev, workspaceName: e.target.value }))}
                  placeholder="Workspace name"
                  className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                />

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="flex items-center gap-2 rounded-xl border border-line bg-slate-50 px-4 py-3 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={asanaForm.syncEnabled}
                      onChange={(e) => setAsanaForm((prev) => ({ ...prev, syncEnabled: e.target.checked }))}
                    />
                    Enable Asana sync
                  </label>
                  <label className="flex items-center gap-2 rounded-xl border border-line bg-slate-50 px-4 py-3 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={asanaForm.includeTemplates}
                      onChange={(e) => setAsanaForm((prev) => ({ ...prev, includeTemplates: e.target.checked }))}
                    />
                    Include templates
                  </label>
                </div>

                {asanaError && <p className="text-xs text-rose-600">{asanaError}</p>}
                {asanaSuccess && <p className="text-xs text-emerald-700">{asanaSuccess}</p>}

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleAsanaTest}
                    disabled={asanaTesting}
                    className="rounded-full border border-line px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                  >
                    {asanaTesting ? "Testing..." : "Test connection"}
                  </button>
                  <button
                    type="submit"
                    disabled={asanaSaving}
                    className="rounded-full bg-moss px-4 py-2 text-sm font-semibold text-white transition hover:bg-moss/90 disabled:opacity-50"
                  >
                    {asanaSaving ? "Saving..." : "Save Asana settings"}
                  </button>
                  {asanaIntegration?.tokenSource === "stored" && (
                    <button
                      type="button"
                      onClick={handleClearStoredAsanaToken}
                      disabled={asanaSaving}
                      className="rounded-full border border-line px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                    >
                      Clear stored token
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-2xl border border-line bg-slate-50 p-4 text-sm">
                  <p className="font-semibold text-slate-900">Connection status</p>
                  <div className="mt-3 space-y-2 text-stone">
                    <p>Token source: <span className="font-semibold text-slate-900">{asanaIntegration?.tokenSource || "none"}</span></p>
                    <p>Token configured: <span className="font-semibold text-slate-900">{asanaIntegration?.hasToken ? "Yes" : "No"}</span></p>
                    <p>Last tested: <span className="font-semibold text-slate-900">{asanaIntegration?.lastTestedAt || "Never"}</span></p>
                    <p>Workspace: <span className="font-semibold text-slate-900">{asanaIntegration?.config.workspaceName || asanaIntegration?.config.workspaceGid || "Not set"}</span></p>
                    <p>Selected projects: <span className="font-semibold text-slate-900">{asanaForm.selectedProjectGids.length}</span></p>
                  </div>
                  {asanaIntegration?.metadata.connectedAccount && (
                    <p className="mt-3 text-xs text-stone">
                      Connected account: <span className="font-semibold text-slate-900">{asanaIntegration.metadata.connectedAccount.name}</span>
                      {asanaIntegration.metadata.connectedAccount.email ? ` (${asanaIntegration.metadata.connectedAccount.email})` : ""}
                    </p>
                  )}
                  {asanaIntegration?.lastError && (
                    <p className="mt-3 text-xs text-rose-600">{asanaIntegration.lastError}</p>
                  )}
                  {asanaIntegration?.metadata.templatesUnavailableReason && (
                    <p className="mt-3 text-xs text-amber-700">{asanaIntegration.metadata.templatesUnavailableReason}</p>
                  )}
                </div>

                <div className="rounded-2xl border border-line bg-white p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">Synced projects</p>
                      <p className="text-xs text-stone">Choose which Asana projects this dashboard should read from first.</p>
                    </div>
                    <StatusPill tone="neutral" label={`${asanaIntegration?.metadata.projects.length || 0} available`} />
                  </div>

                  <div className="mt-4 max-h-72 space-y-2 overflow-y-auto pr-1 text-sm">
                    {(asanaIntegration?.metadata.projects.length || 0) > 0 ? (
                      asanaIntegration?.metadata.projects.map((project) => (
                        <label
                          key={project.gid}
                          className="flex items-start gap-3 rounded-2xl border border-line bg-slate-50 px-4 py-3 text-sm text-slate-700"
                        >
                          <input
                            type="checkbox"
                            checked={asanaForm.selectedProjectGids.includes(project.gid)}
                            onChange={() => toggleAsanaProject(project.gid)}
                          />
                          <div>
                            <p className="font-semibold text-slate-900">{project.name}</p>
                            <p className="text-xs text-stone">{project.teamName || "No team label"}</p>
                          </div>
                        </label>
                      ))
                    ) : (
                      <div className="rounded-2xl border border-dashed border-line px-4 py-6 text-sm text-stone">
                        Run a connection test to load projects for this workspace.
                      </div>
                    )}
                  </div>

                  {asanaIntegration?.config.includeTemplates && (
                    <div className="mt-4 rounded-2xl border border-line bg-slate-50 px-4 py-3 text-sm">
                      <p className="font-semibold text-slate-900">Templates</p>
                      <p className="mt-1 text-xs text-stone">
                        {asanaIntegration.metadata.templates.length > 0
                          ? `${asanaIntegration.metadata.templates.length} project templates discovered.`
                          : "Templates will show here if the workspace exposes them to the API."}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </form>
          </Card>
        )}
      </div>

      {teamMembers.length > 0 && (
        <Card>
          <p className="text-lg font-semibold text-slate-900">Team Members</p>
          <div className="mt-4 space-y-2 text-sm">
            {teamMembers.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-moss/10 text-xs font-medium text-moss">
                    {member.name?.[0] || "?"}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{member.name}</p>
                    <p className="text-xs text-stone">{member.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill
                    tone={member.role === "Admin" ? "success" : member.role === "Staff" ? "neutral" : "warning"}
                    label={member.role}
                  />
                  {member.status !== "active" && (
                    <StatusPill tone="danger" label={member.status} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <InviteUserModal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        onSuccess={() => {
          setIsInviteOpen(false)
          // Refresh team list
          fetch("/api/users").then(r => r.json()).then(d => {
            if (d.users) setTeamMembers(d.users)
          }).catch(() => {})
        }}
      />
    </div>
  )
}

export default SettingsPage
